import PermissionButton from '../../permissions/PermissionButton';
import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Tabs,
  Tag,
  Drawer,
  Form,
  Select,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useListDemarques,
  useCreateDemarque,
} from '../../hooks/avoirHooks';
import { useFetchAllEntrepots, useDefaultEntrepot } from '../../hooks/entrepotHooks';
import { getDefaultEntrepot } from '../../utils/defaultEntrepot';

const { TabPane } = Tabs;


const statusTag = (validated) => (
  validated ? <Tag color="green">Validée</Tag> : <Tag color="orange">Enregistrée</Tag>
);

const DemarqueList = ({ onSelectDemarque, onBack }) => {
  const [activeTab, setActiveTab] = useState('draft');
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const { items: allItems, total, loading, error, refetch } = useListDemarques({ page: 1, pageSize: 1000 });
  const { handleCreate, loading: creating } = useCreateDemarque();
  const { entrepots, loading: loadingEntrepots } = useFetchAllEntrepots();
  const { defaultCode } = useDefaultEntrepot();

  const [form] = Form.useForm();

  const getDateColumn = () => ({
    title: activeTab === 'validated' ? 'Validation Date' : 'Creation Date',
    key: 'date',
    render: (record) => {
      const date = record.VALIDER ? record.DATEVALID : record.DATE_DEM;
      return dayjs(date).format('DD/MM/YYYY');
    },
    sorter: (a, b) => {
      const dateA = a.VALIDER ? a.DATEVALID : a.DATE_DEM;
      const dateB = b.VALIDER ? b.DATEVALID : b.DATE_DEM;
      return dayjs(dateA).unix() - dayjs(dateB).unix();
    },
  });

  const columns = useMemo(() => [
    {
      title: 'Reference',
      dataIndex: 'REF_DEM',
      key: 'REF_DEM',
      render: (value, record) => (
        <Space>
          <span>{value}</span>
          {statusTag(record.VALIDER)}
        </Space>
      ),
    },
    getDateColumn(),
    {
      title: 'Entrepôt',
      key: 'entrepot',
      render: (record) => (
        <span>
          {record.entrepot?.name ? `${record.entrepot.code_ent} - ${record.entrepot.name}` : record.CODE_ENT}
        </span>
      ),
    },
    {
      title: 'Articles',
      dataIndex: 'detailCount',
      key: 'detailCount',
      width: 100,
      render: (value) => value || 0,
    },
    {
      title: 'Remarks',
      dataIndex: 'REMARQUE',
      key: 'REMARQUE',
      ellipsis: true,
    },
  ], [activeTab]);

  const handleStatusChange = (key) => {
    setActiveTab(key);
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleCreateSubmit = async (values) => {
    try {
      const defaultEntrepot = await getDefaultEntrepot();
      const payload = {
        CODE_ENT: values.CODE_ENT || defaultEntrepot,
        REMARQUE: values.REMARQUE || undefined,
        details: [],
      };
      const created = await handleCreate(payload);
      message.success('Démarque créée');
      setCreateDrawerOpen(false);
      form.resetFields();
      await refetch();
      if (created?.REF_DEM && onSelectDemarque) {
        onSelectDemarque(created.REF_DEM);
      }
    } catch (e) {
      message.error(e?.message || "Erreur lors de la création de la démarque");
    }
  };

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    marginBottom: "16px",
    borderBottom: "2px solid #ccc",
    paddingBottom: "10px",
    marginTop: "-10px",
  };

  const tableCardStyle = {
    marginTop: "20px",
    backgroundColor: "#ffffff",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  const getFilteredDemarques = () => {
    if (!allItems) return [];
    let filtered = activeTab === 'validated' 
      ? allItems.filter(d => d.VALIDER)
      : allItems.filter(d => !d.VALIDER);
    
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(d => 
        d.REF_DEM?.toLowerCase().includes(searchLower) ||
        d.REMARQUE?.toLowerCase().includes(searchLower) ||
        d.entrepot?.name?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <Card style={tableCardStyle}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Retour</Button>
          <h2 style={titleStyle}>Demarque List</h2>
        </div>
        <Space>
          <Input.Search
            placeholder="Rechercher"
            allowClear
            onSearch={handleSearch}
            onChange={(e) => {
              const value = e.target.value;
              setSearchText(value);
              if (!value) handleSearch('');
            }}
            value={searchText}
            style={{ width: 220 }}
          />
          <PermissionButton permissionModule="demarque" permissionAction="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.setFieldsValue({ CODE_ENT: defaultCode || undefined });
              setCreateDrawerOpen(true);
            }}
          >
            Create Demarque
          </PermissionButton>
        </Space>
      </Space>
      <Tabs defaultActiveKey="draft" onChange={handleStatusChange}>
        <TabPane tab="Not Validated" key="draft" />
        <TabPane tab="Validated" key="validated" />
      </Tabs>

      <Table
        columns={columns}
        dataSource={getFilteredDemarques()}
        rowKey="REF_DEM"
        pagination={{ pageSize: 10 }}
        onRow={(record) => ({
          onClick: () => onSelectDemarque && onSelectDemarque(record.REF_DEM),
          style: { cursor: 'pointer' }
        })}
      />

      <Drawer
        title="Nouvelle démarque"
        width={480}
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        destroyOnClose
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleCreateSubmit}
          initialValues={{
            CODE_ENT: defaultCode || undefined
          }}
        >
          <Form.Item
            name="CODE_ENT"
            label="Entrepôt"
            rules={[{ required: true, message: 'Sélectionnez un entrepôt' }]}
          >
            <Select
              showSearch
              placeholder="Choisir un entrepôt"
              loading={loadingEntrepots}
              options={(entrepots || []).map((ent) => ({
                value: ent.code_ent,
                label: `${ent.code_ent} - ${ent.name}`,
              }))}
              filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>

          <Form.Item name="REMARQUE" label="Remarque">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <PermissionButton   onClick={() => setCreateDrawerOpen(false)}>Annuler</PermissionButton>
            <PermissionButton permissionModule="demarque" permissionAction="create" type="primary" htmlType="submit" loading={creating}>
              Créer
            </PermissionButton>
          </Space>
        </Form>
      </Drawer>
    </Card>
  );
};

export default DemarqueList;

